import { HttpClient, HttpParams } from "@angular/common/http";
import { Injectable } from "@angular/core";
import { forkJoin, map, Observable, of } from "rxjs";
import { shareReplay, catchError, switchMap } from "rxjs/operators";
import { environment } from "../../../../environments/environment";

const CDN_MASTERDATA_BASE = "https://cdn.lokos.in/lokos-masterdata/";

@Injectable({ providedIn: "root" })
export class ProfilesService {
  constructor(private http: HttpClient) {}

  private clfNameCache = new Map<string, Observable<string>>();

  resolveClfName(clfCode: string): Observable<string> {
    const code = `${clfCode || ""}`.trim();
    if (!code) {
      return of("");
    }
    const cached = this.clfNameCache.get(code);
    if (cached) {
      return cached;
    }
    const url = `${environment.clfApiBaseUrl}api/clf-profile/v1/clf-profile/clf-location`;
    const request$ = this.http
      .post<any>(url, { clfCode: code })
      .pipe(
        map((res: any) => {
          const list = Array.isArray(res?.locations)
            ? res.locations
            : Array.isArray(res?.data?.locations)
              ? res.data.locations
              : Array.isArray(res?.data)
                ? res.data
                : [];
          const hit = list.find(
            (l: any) => `${l?.clf_code ?? l?.clfCode ?? ""}` === code,
          );
          return `${hit?.clf_name ?? hit?.clfName ?? ""}`;
        }),
        catchError(() => of("")),
        shareReplay({ bufferSize: 1, refCount: false }),
      );
    this.clfNameCache.set(code, request$);
    return request$;
  }

  getClfBasicDetailsByCode(clfCode: string): Observable<any> {
    return this.getClfBasicDetailsList({ page: 0, size: 1, clfCode }).pipe(
      map((res: any) => ({
        ...res,
        data: Array.isArray(res?.data?.content) ? res.data.content[0] ?? null : null,
      }))
    );
  }

  getClfBasicDetailsList(query: {
    page: number;
    size: number;
    stateId?: number | string;
    districtId?: number | string;
    blockId?: number | string;
    clfCode?: string;
    approvalStatus?: string;
    clfStatus?: string;
    moduleName?: string;
    sortBy?: string;
    sortDirection?: string;
  }): Observable<any> {
    const url = `${environment.clfApiBaseUrl}api/clf-profile/v1/clf-profile/basic`;
    let params = new HttpParams()
      .set("page", String(query.page))
      .set("size", String(query.size))
      .set("sortBy", query.sortBy || "createdOn")
      .set("sortDirection", query.sortDirection || "DESC");
    if (query.stateId != null && query.stateId !== "") {
      params = params.set("stateId", String(query.stateId));
    }
    if (query.districtId != null && query.districtId !== "") {
      params = params.set("districtId", String(query.districtId));
    }
    if (query.blockId != null && query.blockId !== "") {
      params = params.set("blockId", String(query.blockId));
    }
    if (query.clfCode) {
      params = params.set("clfCode", query.clfCode);
    }
    if (query.approvalStatus) {
      params = params.set("approvalStatus", query.approvalStatus);
    }
    if (query.clfStatus) {
      params = params.set("clfStatus", query.clfStatus);
    }
    params = params.set("moduleName", query.moduleName || "basic-profile");
    return this.http.get(url, { params, observe: "body" });
  }

  getClfProfileById(
    stateShortCode?: string | null,
    clfCode?: string | null
  ): Observable<any> {
    const base = (environment as Record<string, any>)["profilesApiBaseUrl"] || "";
    const shortCode = `${stateShortCode ?? ""}`.toUpperCase();
    const code = `${clfCode ?? ""}`;
    if (!base || !shortCode || !code) {
      return of(null);
    }
    const url = `${base}${shortCode}/clf/${code}`;
    return this.http.get<any>(url, { observe: "body" }).pipe(
      catchError(() => of(null))
    );
  }

  getGeoCodes(
    stateId?: string | number | null,
    districtId?: string | number | null,
    blockId?: string | number | null
  ): Observable<{ stateCode: string; districtCode: string; blockCode: string }> {
    const sid = `${stateId ?? ""}`;
    if (!sid) {
      return of({ stateCode: "", districtCode: "", blockCode: "" });
    }
    const padded = sid.padStart(2, "0");
    const did = `${districtId ?? ""}`;
    const bid = `${blockId ?? ""}`;
    const state$ = this.http.get<any>(`${CDN_MASTERDATA_BASE}statemaster.json?v=2`).pipe(
      map((res: any) => {
        const items = Array.isArray(res) ? res : [];
        const match = items.find((x: any) => `${x?.state_id ?? ""}` === sid);
        return `${match?.state_code ?? ""}`;
      }),
      catchError(() => of(""))
    );
    const district$ = did
      ? this.http.get<any>(`${CDN_MASTERDATA_BASE}state/${padded}.json?v=2`).pipe(
          map((res: any) => {
            const items = Array.isArray(res) ? res : [];
            const match = items.find((x: any) => `${x?.district_id ?? ""}` === did);
            return `${match?.district_code ?? ""}`;
          }),
          catchError(() => of(""))
        )
      : of("");
    const block$ = did && bid
      ? this.http.get<any>(`${CDN_MASTERDATA_BASE}state/${padded}/district/${did}.json?v=2`).pipe(
          map((res: any) => {
            const items = Array.isArray(res) ? res : [];
            const match = items.find((x: any) => `${x?.block_id ?? x?.blockId ?? ""}` === bid);
            return `${match?.block_code ?? match?.blockCode ?? ""}`;
          }),
          catchError(() => of(""))
        )
      : of("");
    return forkJoin({ stateCode: state$, districtCode: district$, blockCode: block$ });
  }

  getGeoStateOptions(): Observable<Array<{ value: string; label: string }>> {
    return this.http.get<any>(`${CDN_MASTERDATA_BASE}statemaster.json?v=2`).pipe(
      map((res: any) => {
        const items = Array.isArray(res) ? res : [];
        return items
          .map((st: any) => ({
            value: `${st?.state_id ?? ""}`,
            label: `${st?.state_name_en ?? ""}` || `${st?.state_id ?? ""}`,
          }))
          .sort((a: { label: string }, b: { label: string }) =>
            a.label.localeCompare(b.label)
          );
      })
    );
  }

  getGeoDistrictOptions(stateId: string | number): Observable<Array<{ value: string; label: string }>> {
    const padded = `${stateId}`.padStart(2, "0");
    return this.http.get<any>(`${CDN_MASTERDATA_BASE}state/${padded}.json?v=2`).pipe(
      map((res: any) => {
        const items = Array.isArray(res) ? res : [];
        return items
          .map((d: any) => ({
            value: `${d?.district_id ?? ""}`,
            label: `${d?.district_name_en ?? ""}` || `${d?.district_id ?? ""}`,
          }))
          .sort((a: { label: string }, b: { label: string }) =>
            a.label.localeCompare(b.label)
          );
      })
    );
  }

  getGeoBlockOptions(stateId?: string | number, districtId?: string | number): Observable<Array<{ value: string; label: string }>> {
    if (stateId == null || stateId === "" || districtId == null || districtId === "") {
      return of([]);
    }
    const padded = `${stateId}`.padStart(2, "0");
    const url = `${CDN_MASTERDATA_BASE}state/${padded}/district/${districtId}.json?v=2`;
    return this.http.get<any>(url).pipe(
      map((res: any) => {
        const items = Array.isArray(res) ? res : [];
        const seen = new Set<string>();
        const options: Array<{ value: string; label: string }> = [];
        items.forEach((b: any) => {
          const id = `${b?.block_id ?? b?.blockId ?? ""}`;
          const name = `${b?.block_name_en ?? b?.block_name ?? b?.blockName ?? ""}`;
          if (!id || seen.has(id)) {
            return;
          }
          seen.add(id);
          options.push({ value: id, label: name || id });
        });
        options.sort((a, b) => a.label.localeCompare(b.label));
        return options;
      })
    );
  }

  getClfUserMappingByUserId(userId: string): Observable<any> {
    const url = `${environment.clfApiBaseUrl}api/clf-profile/v1/clf-profile/clf-user-mapping`;
    return this.http.get(url, { params: { userId }, observe: "body" });
  }

  updateBasicDetails(clfCode: string, body: any): Observable<any> {
    const url = `${environment.clfApiBaseUrl}api/clf-profile/v1/clf-profile/basic/${clfCode}`;
    const params = new HttpParams().set("moduleName", "basic-profile");
    return this.http.put(url, body, { params, observe: "body" });
  }

  modifyApprovalStatusBasicDetails(clfCode: string, body: any): Observable<any> {
    const url = `${environment.clfApiBaseUrl}api/clf-profile/v1/clf-profile/basic/${clfCode}/status`;
    const params = new HttpParams().set("moduleName", "basic-profile");
    return this.http.put(url, body, { params, observe: "body" });
  }

  getFormUpdateHistory(clfCode: string, moduleName = "basic-profile", srpId = ""): Observable<any> {
    const url = `${environment.clfApiBaseUrl}api/clf-profile/v1/clf-profile/history`;
    let params = new HttpParams().set("moduleName", moduleName);
    if (clfCode) {
      params = params.set("clfCode", clfCode);
    }
    if (srpId) {
      params = params.set("srpId", srpId);
    }
    return this.http.get(url, { params, observe: "body" });
  }

  recordStatusChange(
    clfCode: string,
    moduleName: string,
    approvalStatus: string,
    opts: { srpId?: string; updateReason?: string; rejectionReason?: string } = {},
  ): Observable<any> {
    const code = `${clfCode || ""}`;
    const base = `${environment.clfApiBaseUrl}api/clf-profile/v1/clf-profile`;
    const historyBody = {
      clf_code: code,
      srp_id: `${opts.srpId || ""}`,
      module_name: moduleName,
      update_reason: `${opts.updateReason || ""}`,
      rejection_reason: `${opts.rejectionReason || ""}`,
      approval_status: approvalStatus,
    };
    const history$ = this.http
      .put(`${base}/history`, historyBody, { observe: "body" })
      .pipe(catchError(() => of(null)));
    if (!code) {
      return history$;
    }
    const statusParams = new HttpParams()
      .set("moduleName", moduleName)
      .set("approvalStatus", approvalStatus);
    return history$.pipe(
      switchMap(() =>
        this.http
          .put(`${base}/basic/${code}/module-status`, null, {
            params: statusParams,
            observe: "body",
          })
          .pipe(catchError(() => of(null))),
      ),
    );
  }

  getProfileStatusCount(query: {
    stateId?: number | string;
    districtId?: number | string;
    blockId?: number | string;
    clfCode?: string;
    clfStatus?: string;
  }): Observable<any> {
    const url = `${environment.clfApiBaseUrl}api/clf-profile/v1/clf-profile/status-count`;
    let params = new HttpParams();
    if (query.stateId != null && query.stateId !== "") {
      params = params.set("stateId", String(query.stateId));
    }
    if (query.districtId != null && query.districtId !== "") {
      params = params.set("districtId", String(query.districtId));
    }
    if (query.blockId != null && query.blockId !== "") {
      params = params.set("blockId", String(query.blockId));
    }
    if (query.clfCode) {
      params = params.set("clfCode", query.clfCode);
    }
    if (query.clfStatus) {
      params = params.set("clfStatus", query.clfStatus);
    }
    return this.http.get(url, { params, observe: "body" });
  }

  getAnchorCmtcStatus(query: {
    stateId?: number | string;
    districtId?: number | string;
    blockId?: number | string;
    clfCode?: string;
  }): Observable<any> {
    const url = `${environment.clfApiBaseUrl}api/clf-profile/v1/clf-profile/status-count/anchor-cmtc-status`;
    let params = new HttpParams();
    if (query.stateId != null && query.stateId !== "") {
      params = params.set("stateId", String(query.stateId));
    }
    if (query.districtId != null && query.districtId !== "") {
      params = params.set("districtId", String(query.districtId));
    }
    if (query.blockId != null && query.blockId !== "") {
      params = params.set("blockId", String(query.blockId));
    }
    if (query.clfCode) {
      params = params.set("clfCode", query.clfCode);
    }
    return this.http.get(url, { params, observe: "body" });
  }

  getProfileLastUpdated(query: {
    stateId?: number | string;
    districtId?: number | string;
    blockId?: number | string;
    clfCode?: string;
  }): Observable<any> {
    const url = `${environment.clfApiBaseUrl}api/clf-profile/v1/clf-profile/status-count/last-updated`;
    let params = new HttpParams();
    if (query.stateId != null && query.stateId !== "") {
      params = params.set("state_id", String(query.stateId));
    }
    if (query.districtId != null && query.districtId !== "") {
      params = params.set("district_id", String(query.districtId));
    }
    if (query.blockId != null && query.blockId !== "") {
      params = params.set("block_id", String(query.blockId));
    }
    if (query.clfCode) {
      params = params.set("clf_code", query.clfCode);
    }
    return this.http.get(url, { params, observe: "body" });
  }
}
